import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSpinner, FaUserGraduate, FaCertificate, FaChartLine, FaMoneyBillWave, FaEye, FaEnvelope } from 'react-icons/fa';
import api from '../utils/api'; // Assuming you have an api utility for authenticated requests
import { useAuth } from '../context/AuthContext';

const InstructorDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        totalStudents: 0,
        activeCourses: 0,
        certificatesIssued: 0,
        totalRevenue: 0
    });
    const [students, setStudents] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState('all');
    const [courses, setCourses] = useState([]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            if (user?.role !== 'instructor') {
                setError('Access denied. You must be an instructor.');
                setLoading(false);
                return;
            }
            try {
                const { data } = await api.get('/instructors/dashboard');
                setStats(data.stats);
                setCourses(data.courses);
                setStudents(data.students);
            } catch (err) {
                setError('Failed to load dashboard data. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (user) {
            fetchDashboardData();
        }
    }, [user]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount);
    };

    const handleIssueCertificate = async (studentId, courseId) => {
        try {
            await api.post(`/courses/${courseId}/complete`, { studentId });
            // Refresh dashboard data after issuing certificate
            const { data } = await api.get('/instructors/dashboard');
            setStats(data.stats);
            setStudents(data.students);
        } catch (err) {
            console.error('Error issuing certificate:', err);
            setError('Failed to issue certificate. Please try again.');
        }
    };

    const handleViewCertificate = (studentId, courseId, studentName, courseTitle) => {
        // Navigate to certificate preview/download
        navigate(`/certificate/${courseId}/${studentId}`, {
            state: { studentName, courseTitle }
        });
    };

    const handleContactStudent = (email) => {
        window.location.href = `mailto:${email}`;
    };

    const filteredStudents = selectedCourse === 'all'
        ? students
        : students.filter(student => student.courseId === selectedCourse);

    if (loading) {
        return (
            <div className="container py-5 text-center" style={{ marginTop: '75px' }}>
                <FaSpinner className="fa-spin fa-3x text-primary mb-3" />
                <h4>Loading dashboard...</h4>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container py-5 text-center" style={{ marginTop: '75px' }}>
                <h4 className="text-danger">{error}</h4>
                <button 
                    className="btn btn-primary mt-3"
                    onClick={() => navigate('/dashboard')}
                >
                    Back to Dashboard
                </button>
            </div>
        );
    }

    return (
        <div className="container py-5" style={{ marginTop: '75px' }}>
            <h1 className="mb-4" style={{ fontSize: '2.5rem', fontWeight: '700', letterSpacing: '1px' }}>Instructor Dashboard</h1>
            
            {/* Stats Cards */}
            <div className="row mb-4">
                <div className="col-md-3 mb-3">
                    <div className="card bg-primary text-white h-100">
                        <div className="card-body">
                            <h5 className="card-title">
                                <FaUserGraduate className="me-2" />
                                Total Students
                            </h5>
                            <h2>{stats.totalStudents}</h2>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="card bg-success text-white h-100">
                        <div className="card-body">
                            <h5 className="card-title">
                                <FaChartLine className="me-2" />
                                Active Courses
                            </h5>
                            <h2>{stats.activeCourses}</h2>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="card bg-info text-white h-100">
                        <div className="card-body">
                            <h5 className="card-title">
                                <FaCertificate className="me-2" />
                                Certificates Issued
                            </h5>
                            <h2>{stats.certificatesIssued}</h2>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="card bg-purple text-white h-100">
                        <div className="card-body">
                            <h5 className="card-title">
                                <FaMoneyBillWave className="me-2" />
                                Total Revenue
                            </h5>
                            <h2>{formatCurrency(stats.totalRevenue)}</h2>
                        </div>
                    </div>
                </div>
            </div>

            {/* Course Filter */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row align-items-center">
                        <div className="col-md-6">
                            <h5 className="mb-0">Student Progress</h5>
                        </div>
                        <div className="col-md-6">
                            <select 
                                className="form-select"
                                value={selectedCourse}
                                onChange={(e) => setSelectedCourse(e.target.value)}
                            >
                                <option value="all">All Courses</option>
                                {courses.map(course => (
                                    <option key={course.id} value={course.id}>
                                        {course.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Students Table */}
            <div className="card">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Student</th>
                                    <th>Email</th>
                                    <th>Course</th>
                                    <th>Progress</th>
                                    <th>Certificate</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStudents.length > 0 ? (
                                    filteredStudents.map(student => (
                                        <tr key={`${student.id}-${student.courseId}`}>
                                            <td>{student.name}</td>
                                            <td>{student.email}</td>
                                            <td>{student.courseTitle}</td>
                                            <td>
                                                <div className="progress">
                                                    <div 
                                                        className="progress-bar" 
                                                        role="progressbar" 
                                                        style={{ width: `${student.progress}%` }}
                                                    >
                                                        {student.progress}%
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {student.certificateIssued ? (
                                                    <span className="badge bg-success">
                                                        <FaCertificate className="me-1" />
                                                        Issued
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-secondary">
                                                        Pending
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    {student.progress === 100 && !student.certificateIssued && (
                                                        <button
                                                            className="btn btn-sm btn-primary"
                                                            onClick={() => handleIssueCertificate(student.id, student.courseId)}
                                                            title="Issue Certificate"
                                                        >
                                                            <FaCertificate className="me-1" />
                                                            Issue
                                                        </button>
                                                    )}
                                                    {student.certificateIssued && (
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => handleViewCertificate(student.id, student.courseId, student.name, student.courseTitle)}
                                                            title="View Certificate"
                                                        >
                                                            <FaEye />
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => handleContactStudent(student.email)}
                                                        title="Contact Student"
                                                    >
                                                        <FaEnvelope />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4">No students to display for this filter.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InstructorDashboard; 